/**
 * useTrustState — derives a member-visible "trust profile" from the
 * authoritative columns we already pull (profile, KYC, BGC, profile
 * completion, recent behavioral risk).
 *
 * Frontend rule mirrors entitlements: this hook is presentation-only.
 * The DB enforces the real gates via RLS + RPC. The matrix exists to
 * tell the member *why* an action is unavailable and what to do about
 * it — never to authorize the action itself.
 *
 * Risk surfaces from the last 14 days of moderation_log "warn" / "block"
 * entries on the current user (RLS: moderation_log_self_read). We
 * collapse them into low / elevated / high so the matrix can show a
 * single chip instead of an opaque count.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import type { TierId } from "@/lib/profile/types";

export type RiskLevel = "low" | "elevated" | "high";

export interface TrustState {
  ready: boolean;
  // Authoritative gate flags ↓
  kycVerified: boolean;
  kycPending: boolean;
  hasLegalName: boolean;
  emailConfirmed: boolean;
  hasEmergencyContact: boolean;
  bgcVerified: boolean;
  communityVerified: boolean;
  profileComplete: boolean;
  hasMinimumPhotos: boolean;
  acknowledgedEmergency: boolean;
  tier: TierId;
  // Synthesised ↓
  riskLevel: RiskLevel;
  riskRecentCount: number;
  trustScore: number; // 0–100, presentation only
}

const RISK_WINDOW_DAYS = 14;

export const useTrustState = (): TrustState => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [warnCount, setWarnCount] = useState(0);
  const [blockCount, setBlockCount] = useState(0);
  const [riskReady, setRiskReady] = useState(false);

  useEffect(() => {
    if (!user) {
      setWarnCount(0);
      setBlockCount(0);
      setRiskReady(true);
      return;
    }
    const since = new Date(
      Date.now() - RISK_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
    let cancelled = false;
    void (async () => {
      const { data } = await (supabase as any)
        .from("moderation_log")
        .select("severity")
        .eq("member_id", user.id)
        .gte("created_at", since)
        .in("severity", ["warn", "block"]);
      if (cancelled) return;
      const rows = (data ?? []) as { severity: string }[];
      setWarnCount(rows.filter((r) => r.severity === "warn").length);
      setBlockCount(rows.filter((r) => r.severity === "block").length);
      setRiskReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return useMemo<TrustState>(() => {
    const ready = !!profile && riskReady;
    const photos = profile?.photos ?? [];
    const prompts = profile?.prompts ?? [];

    const hasMinimumPhotos = photos.length >= 3;
    const profileComplete =
      hasMinimumPhotos &&
      prompts.length >= 3 &&
      !!profile?.display_name &&
      !!profile?.bio;

    const riskLevel: RiskLevel =
      blockCount > 0 || warnCount >= 4
        ? "high"
        : warnCount >= 2
          ? "elevated"
          : "low";

    // Presentation-only score. Weighted toward the gates the member
    // can actually act on.
    let score = 0;
    if (profile?.kyc_status === "verified") score += 35;
    else if (profile?.kyc_status === "pending") score += 10;
    if (profile?.bgc_verified) score += 10;
    if (profile?.community_verified) score += 10;
    if (profileComplete) score += 15;
    if (profile?.emergency_contact_phone) score += 10;
    if (profile?.acknowledged_emergency_at) score += 5;
    if (user?.email_confirmed_at) score += 10;
    // Risk haircut
    if (riskLevel === "elevated") score = Math.max(0, score - 10);
    if (riskLevel === "high") score = Math.max(0, score - 25);
    score = Math.min(100, score);

    return {
      ready,
      kycVerified: profile?.kyc_status === "verified",
      kycPending: profile?.kyc_status === "pending",
      hasLegalName: !!(profile?.legal_first_name && profile?.legal_last_name),
      emailConfirmed: !!user?.email_confirmed_at,
      hasEmergencyContact: !!profile?.emergency_contact_phone,
      bgcVerified: !!profile?.bgc_verified,
      communityVerified: !!profile?.community_verified,
      profileComplete,
      hasMinimumPhotos,
      acknowledgedEmergency: !!profile?.acknowledged_emergency_at,
      tier: (profile?.tier ?? "tier_signal") as TierId,
      riskLevel,
      riskRecentCount: warnCount + blockCount,
      trustScore: score,
    };
  }, [profile, user, warnCount, blockCount, riskReady]);
};
